/**
 * Tab default de `/patients` cuando la URL no trae `tab`.
 *
 * Documentado en docs/PATIENTS_DEFAULT_TAB.md
 */

import { HUB_RISK_PATIENTS_MIN } from './clinicHubTopSignals';

/** Mínimo de no-volvieron para aterrizar en Riesgo (igual que Top 3 del hub). */
export const PATIENTS_DEFAULT_NO_RETURN_MIN = HUB_RISK_PATIENTS_MIN;

/** Δ tasa de recurrencia (pp) vs mes anterior → Riesgo. */
export const PATIENTS_DEFAULT_RECURRENCE_DROP_PP = -5;

export type PatientsTab = 'network' | 'overview' | 'acquisition' | 'retention' | 'risk';

export type PatientsDefaultReason = 'url' | 'network' | 'no_return' | 'recurrence_drop' | 'healthy';

export interface ResolvePatientsDefaultTabInput {
  /** Query `tab`; `null` / inválido = no forzado. */
  urlTab: string | null;
  /** ≥2 clínicas en scope. */
  multiClinic?: boolean;
  /** Hay `tenant` en la URL (modo clínica, no red). */
  hasTenantInUrl?: boolean;
  canNoReturn: boolean;
  noReturnTotal: number | null;
  canRecurrence: boolean;
  tasaRecurrencia: number | null;
  previousTasaRecurrencia: number | null;
}

export interface PatientsDefaultTabResult {
  tab: PatientsTab;
  reason: PatientsDefaultReason;
  recurrenceDeltaPp: number | null;
}

export function parseExplicitPatientsTab(raw: string | null, multiClinic = true): PatientsTab | null {
  if (raw === 'network') return multiClinic ? 'network' : null;
  if (raw === 'overview' || raw === 'acquisition' || raw === 'retention' || raw === 'risk') {
    return raw;
  }
  return null;
}

function recurrenceDeltaPp(current: number | null, previous: number | null): number | null {
  if (current == null || previous == null) return null;
  return Number((current - previous).toFixed(1));
}

/**
 * URL gana siempre. Sin `tab`, Riesgo si no-retorno ≥ umbral o caída fuerte de recurrencia.
 * Si no, overview. No escribe la URL: el caller solo aplica el tab en memoria.
 */
export function resolvePatientsDefaultTab(input: ResolvePatientsDefaultTabInput): PatientsDefaultTabResult {
  const explicit = parseExplicitPatientsTab(input.urlTab, Boolean(input.multiClinic));
  if (explicit) {
    return { tab: explicit, reason: 'url', recurrenceDeltaPp: null };
  }

  if (input.multiClinic && !input.hasTenantInUrl) {
    return { tab: 'network', reason: 'network', recurrenceDeltaPp: null };
  }

  if (input.canNoReturn && input.noReturnTotal != null && input.noReturnTotal >= PATIENTS_DEFAULT_NO_RETURN_MIN) {
    return { tab: 'risk', reason: 'no_return', recurrenceDeltaPp: null };
  }

  const delta = recurrenceDeltaPp(input.tasaRecurrencia, input.previousTasaRecurrencia);
  if (input.canRecurrence && delta != null && delta <= PATIENTS_DEFAULT_RECURRENCE_DROP_PP) {
    return { tab: 'risk', reason: 'recurrence_drop', recurrenceDeltaPp: delta };
  }

  return { tab: 'overview', reason: 'healthy', recurrenceDeltaPp: delta };
}
